import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import {
  RotateCcw,
  Search,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  User,
  Clock,
  RefreshCw,
  CheckCircle,
  TrendingDown,
  Layers,
  Lock,
  History,
  XCircle,
  Loader2,
} from "lucide-react";
import PurchaseItemsReturnedModal from "./PurchaseItemsReturnedModal";
import AddPurchaseReturnModal from "./AddPurchaseReturnModal";
import PurchaseDebitMemoModal from "./PurchaseDebitMemoModal";
import { domain, domainAccounting } from "../../../../security";
import Pagination from "../../Pagination";
import Loader from "../../../loader/Loader";
import { selectUserID } from "../../../../redux/IchthusSlice";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const StatCard = ({ label, value, Icon, colorClass }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
    <div className={`p-3 rounded-lg ${colorClass}`}>
      <Icon size={22} />
    </div>
    <div>
      <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wide">
        {label}
      </p>
      <p className="text-xl font-black text-slate-800">{value}</p>
    </div>
  </div>
);
// ─────────────────────────────────────────────────────────────────────────────
// HISTORY MODAL
// ─────────────────────────────────────────────────────────────────────────────
const PurchaseReturnHistoryModal = ({ isOpen, onClose }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${domain}/api/PurchaseReturns/history?search=${search}`,
      );
      setHistoryData(res.data || []);
    } catch {
      toast.error("Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
      setExpandedRow(null);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const paginated = historyData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <History size={18} />
            </div>
            Deleted Returns History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Return #..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <History size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No deleted return records found.</p>
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest sticky top-0">
                <tr>
                  <th className="px-5 py-3.5 w-8"></th>
                  <th className="px-5 py-3.5">Return #</th>
                  <th className="px-5 py-3.5">Source GR</th>
                  <th className="px-5 py-3.5">Vendor</th>
                  <th className="px-5 py-3.5 text-right">Refund Amount</th>
                  <th className="px-5 py-3.5">Created By</th>
                  <th className="px-5 py-3.5">Deleted By</th>
                  <th className="px-5 py-3.5">Deleted On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.map((ret) => (
                  <React.Fragment key={ret.id}>
                    <tr
                      onClick={() =>
                        setExpandedRow(expandedRow === ret.id ? null : ret.id)
                      }
                      className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 text-slate-400">
                        {expandedRow === ret.id ? (
                          <ChevronDown size={15} />
                        ) : (
                          <ChevronRight size={15} />
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-black text-slate-700 font-mono text-xs">
                          {ret.returnNumber}
                        </div>
                        <span
                          className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded mt-1 inline-block border ${ret.isProcessed ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}
                        >
                          {ret.isProcessed ? "Was Processed" : "Was Pending"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-bold text-indigo-700">
                          {ret.goodReceiptNumber}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 text-slate-700 font-semibold">
                          <User size={12} className="text-slate-400 shrink-0" />{" "}
                          {ret.vendorName}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right font-black text-red-500">
                        {formatCurrency(ret.totalRefundAmount)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-slate-700">
                          {ret.createdByFullName || "—"}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {formatDate(ret.createdDate)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-red-600">
                          {ret.deletedByFullName || "—"}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-xs text-slate-600 flex items-center gap-1">
                          <Clock size={10} className="text-slate-400" />{" "}
                          {formatDate(ret.deletedDate)}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded items */}
                    {expandedRow === ret.id && (
                      <tr className="bg-slate-50/50">
                        <td colSpan={8} className="px-5 py-4 pl-16">
                          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                            <p className="text-[10px] font-black uppercase text-slate-400 mb-3 flex items-center gap-1.5">
                              <Package size={12} /> Items in this return
                            </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-50 text-[9px] uppercase text-slate-500 font-black">
                                  <th className="p-2 pl-3 text-left">
                                    Product
                                  </th>
                                  <th className="p-2 text-center">
                                    Qty Returned
                                  </th>
                                  <th className="p-2 text-right pr-3">
                                    Refund Value
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {(ret.items || []).map((item, idx) => (
                                  <tr key={idx}>
                                    <td className="p-2 pl-3 font-bold text-slate-700">
                                      {item.productName}
                                    </td>
                                    <td className="p-2 text-center font-black text-slate-600">
                                      {item.quantityReturned}
                                    </td>
                                    <td className="p-2 text-right pr-3 font-black text-red-500">
                                      {formatCurrency(item.refundAmount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {ret.reason && (
                              <div className="mt-3 pt-2 border-t border-slate-100 text-xs flex gap-2">
                                <span className="font-black uppercase text-slate-400">
                                  Reason:
                                </span>
                                <span className="text-slate-600 italic">
                                  {ret.reason}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && historyData.length > 0 && (
          <div className="shrink-0 border-t border-slate-100 px-4 py-3">
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={historyData.length}
              currentPage={currentPage}
              paginate={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function AllPurchaseReturns() {
  const currentUserId = useSelector(selectUserID);

  const [returnsData, setReturnsData] = useState([]);
  const [sorData, setSorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Returns");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  // Detail caching state (Optimization)
  const [detailsCache, setDetailsCache] = useState({});
  const [fetchingDetailsId, setFetchingDetailsId] = useState(null);
  const [fetchingModalId, setFetchingModalId] = useState(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isDebitMemoOpen, setIsDebitMemoOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [returnsRes, sorRes] = await Promise.all([
        axios.get(`${domain}/api/PurchaseReturns`),
        axios.get(`${domain}/api/SalesOrderReturns`),
      ]);
      setReturnsData(returnsRes.data || []);
      setSorData(sorRes.data || []);
    } catch {
      toast.error("Failed to fetch return records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // OPTIMIZATION: Fetch single detailed record by ID when needed
  const fetchReturnDetails = async (ret) => {
    if (detailsCache[ret.id]) return detailsCache[ret.id];
    if (ret.items) return ret; // fallback if already attached

    try {
      const res = await axios.get(`${domain}/api/PurchaseReturns/${ret.id}`);
      setDetailsCache((prev) => ({ ...prev, [ret.id]: res.data }));
      return res.data;
    } catch (err) {
      toast.error("Failed to load item details.");
      return null;
    }
  };

  const handleExpandRow = async (ret) => {
    if (expandedRow === ret.id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(ret.id);
      setFetchingDetailsId(ret.id);
      await fetchReturnDetails(ret);
      setFetchingDetailsId(null);
    }
  };

  // Open items modal specifically fetches items via ID first
  const openItemsModal = async (ret) => {
    setFetchingModalId(ret.id);
    const fullData = await fetchReturnDetails(ret);
    setFetchingModalId(null);

    if (fullData) {
      setSelectedReturn(fullData);
      setIsItemsModalOpen(true);
    }
  };

  const openDebitMemo = async (ret) => {
    const fullData = await fetchReturnDetails(ret);
    setSelectedReturn(fullData || ret);
    setIsDebitMemoOpen(true);
  };

  const draftReturns = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return returnsData
      // .filter((r) => !r.isProcessed)
      .filter(
        (r) =>
          !t ||
          (r.returnNumber || "").toLowerCase().includes(t) ||
          (r.goodReceiptNumber || "").toLowerCase().includes(t) ||
          (r.vendorName || "").toLowerCase().includes(t),
      );
  }, [returnsData, searchTerm]);

  const processedReturns = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return sorData.filter(
      (r) =>
        !t ||
        (r.salesOrderReturnNumber || "").toLowerCase().includes(t) ||
        (r.goodReceiptNumber || "").toLowerCase().includes(t) ||
        (r.vendorName || "").toLowerCase().includes(t),
    );
  }, [sorData, searchTerm]);

  const totalRefunded = processedReturns.reduce(
    (a, b) => a + (b.totalRefundAmount || 0),
    0,
  );
  // Using cache fallback logic if replacements aren't directly available
  const totalReplacements = processedReturns.filter((r) => {
    const items = r.items || detailsCache[r.id]?.items || [];
    return items.some((i) => i.replaceQty > 0);
  }).length;

  const handleDeleteDraft = async (ret) => {
    if (
      !window.confirm("⚠ Deleting this return will reverse changes. Continue?")
    )
      return;
    setPendingDeleteId(ret.id);
    try {
      await axios.put(`${domain}/api/PurchaseReturns/soft-delete/${ret.id}`, {
        UserID: currentUserId,
      });
      toast.success("Return deleted successfully.");
      fetchData();
    } catch (err) {
      toast.error("Delete failed.");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleDeleteSOR = async (sor) => {
    if (!window.confirm("⚠ This will delete the Sales Order Return. Continue?"))
      return;
    setPendingDeleteId(sor.id);
    try {
      await axios.put(`${domain}/api/SalesOrderReturns/soft-delete/${sor.id}`, {
        UserID: currentUserId,
      });
      toast.success("SOR deleted.");
      fetchData();
    } catch (err) {
      toast.error("Delete failed.");
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    setCurrentPage(1);
    setExpandedRow(null);
    setSearchTerm("");
  };

  const currentList = activeTab === "Returns" ? draftReturns : processedReturns;
  const paginated = currentList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    if(returnsData.length <= 0){
      toast.error(`No Returns to Sync.`);
      return
    }

    setLoading(true)
    try {
      const getDateOnly = value => {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };

      const filtered = returnsData.filter(i => {
        const date = getDateOnly(i.returnDate);
        const from = getDateOnly(dateFrom);
        const to = getDateOnly(dateTo);

        return date >= from && date <= to;
      });

      // const poPayload = filtered?.filter(i => i.creationDate >= dateFrom && i.creationDate <= dateTo && !i.isStaging).map(po => {
      const _data = filtered?.filter(i => i.isProcessed).map(i => {
        const _purchaseReturnDetails = i.items?.map((detail) => {
          return ({
            itemCode: detail.itemCode,
            totalQuantity: detail.quantityReturned ?? 0,
            cost: (detail.refundAmount ?? 0) / (detail.quantityReturned ?? 0),
            totalAmount: detail.refundAmount ?? 0,
            refundQuantity: detail.cashQty ?? 0,
            replacementQuantity: detail.replaceQty ?? 0,
          })
        })

        return {
          grNumber: i.goodReceiptNumber ?? "",
          returnNumber: i.returnNumber ?? "", //temporary
          returnDate: i.returnDate ?? null,
          status: "Posted",
          notes: i.reason ?? "",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
          purchaseReturnDetails: _purchaseReturnDetails,
        }
      });

      console.log({_data})

      await axios.post(`${domainAccounting}/api/PurchaseReturn/bulk`, _data);
      toast.success(`Successfully pushed ${_data.length || 0} to Accounting!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push "return" to Accounting.`);
    } finally {
      setLoading(false)
    }
  };

  return (
    <div className="min-h-screen  pb-14">
      <ToastContainer autoClose={2500} position="top-right" />

      {/* Modals */}
      {isAddModalOpen && (
        <AddPurchaseReturnModal
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            fetchData();
          }}
          currentUserId={currentUserId}
        />
      )}
      {isItemsModalOpen && selectedReturn && (
        <PurchaseItemsReturnedModal
          returnRecord={selectedReturn}
          onClose={() => {
            setIsItemsModalOpen(false);
            setSelectedReturn(null);
          }}
          onSuccess={() => {
            setIsItemsModalOpen(false);
            setSelectedReturn(null);
            // setActiveTab("ForSalesOrder");
            fetchData();
          }}
          currentUserId={currentUserId}
        />
      )}
      {isDebitMemoOpen && selectedReturn && (
        <PurchaseDebitMemoModal
          returnRecord={selectedReturn}
          onClose={() => {
            setIsDebitMemoOpen(false);
            setSelectedReturn(null);
          }}
        />
      )}
      <PurchaseReturnHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <RotateCcw className="text-red-600" size={24} /> Purchase Returns
            </h1>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              Returns History (GR) • Session User: {currentUserId}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openSyncToAccountingForm()}
              className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
            >
              <History size={16} />
              <span className="hidden sm:inline">Sync To Accounting</span>
            </button>

            <button
              onClick={() => setIsHistoryOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-bold transition border border-slate-200"
            >
              <History size={15} /> History
            </button>
            {activeTab === "Returns" && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition shadow-sm"
              >
                <Plus size={16} /> Process New Return
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[95%] mx-auto px-4 mt-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Returns Logged"
            value={returnsData.length}
            Icon={Layers}
            colorClass="bg-indigo-50 text-indigo-600"
          />
          <StatCard
            label="Pending Processing"
            value={draftReturns?.filter(i => !i.isProcessed)?.length}
            Icon={Clock}
            colorClass="bg-amber-50 text-amber-600"
          />
          <StatCard
            label="Total Cash Refunded"
            value={formatCurrency(totalRefunded)}
            Icon={TrendingDown}
            colorClass="bg-red-50 text-red-600"
          />
          <StatCard
            label="With Replacements"
            value={totalReplacements}
            Icon={RefreshCw}
            colorClass="bg-blue-50 text-blue-600"
          />
        </div>

        {/* Tabs */}
        <div className="hidden items-center gap-2 bg-slate-200/60 p-1.5 rounded-xl w-fit border border-slate-200 ">
          {[
            {
              id: "Returns",
              label: "Returns",
              icon: RotateCcw,
              badge: draftReturns.length,
            },
            {
              id: "ForSalesOrder",
              label: "For Sales Order",
              icon: FileText,
              badge: processedReturns.length,
            },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? "bg-white text-indigo-700 shadow-sm ring-1 ring-black/5"
                    : "text-slate-600 hover:bg-white/50"
                }`}
              >
                <tab.icon
                  size={15}
                  className={isActive ? "text-indigo-600" : "text-slate-400"}
                />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder={
                activeTab === "Returns"
                  ? "Search by Return #, GR #, or Vendor..."
                  : "Search by SGR-R #, GR #, or Vendor..."
              }
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* ── RETURNS TAB (DRAFTS) ── */}
            {activeTab === "Returns" && (
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest">
                  <tr>
                    <th className="px-5 py-3.5 w-10"></th>
                    <th className="px-5 py-3.5">Return #</th>
                    <th className="px-5 py-3.5">Source GR</th>
                    <th className="px-5 py-3.5">Vendor</th>
                    <th className="px-5 py-3.5">Reason</th>
                    <th className="px-5 py-3.5">Created By</th>
                    <th className="px-5 py-3.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-14 text-slate-400 text-sm"
                      >
                        No pending return records.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((ret) => (
                      <React.Fragment key={ret.id}>
                        <tr
                          onClick={() => handleExpandRow(ret)}
                          className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                        >
                          <td className="px-5 py-4 text-slate-400">
                            {expandedRow === ret.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-black text-slate-800 font-mono text-xs">
                              {ret.returnNumber}
                            </div>
                            <span className={`text-[9px] font-black uppercase ${ret.isProcessed ? "bg-green-100 text-green-700  border-green-200" : "bg-amber-100 text-amber-700  border-amber-200"} px-1.5 py-0.5 rounded mt-1 inline-block border`}>
                              {`${ret.isProcessed ? "Already Processed" : "Pending"}`}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="font-bold text-indigo-700 text-sm">
                              {ret.goodReceiptNumber}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 text-slate-700 font-semibold">
                              <User
                                size={12}
                                className="text-slate-400 shrink-0"
                              />{" "}
                              {ret.vendorName}
                            </div>
                          </td>
                          <td className="px-5 py-4 max-w-[180px]">
                            <p className="text-xs text-slate-500 italic truncate">
                              {ret.reason || "—"}
                            </p>
                          </td>
                          <td className="px-5 py-4">
                            <div className="text-xs font-semibold text-slate-700">
                              {ret.createdByFullName || "—"}
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock size={9} /> {formatDate(ret.createdDate)}
                            </div>
                          </td>
                          <td
                            className="px-5 py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!ret.isProcessed ?
                              <div className="flex items-center justify-center gap-2">
                                {/* Process Items Button calls ID API Fetch */}
                                <button
                                  onClick={() => openItemsModal(ret)}
                                  disabled={fetchingModalId === ret.id}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-bold uppercase hover:bg-indigo-700 transition shadow-sm disabled:opacity-50"
                                >
                                  {fetchingModalId === ret.id ? (
                                    <Loader2 size={13} className="animate-spin" />
                                  ) : (
                                    <Package size={13} />
                                  )}
                                  Items Returned
                                </button>
                                <button
                                  onClick={() => handleDeleteDraft(ret)}
                                  disabled={pendingDeleteId === ret.id}
                                  className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                              :
                              <div className="flex items-center justify-center gap-2">—</div>
                            }
                          </td>
                        </tr>

                        {/* Expanded Breakdown via ID fetch */}
                        {expandedRow === ret.id && (
                          <tr className="bg-slate-50/50">
                            <td colSpan={7} className="px-5 py-4 pl-16">
                              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm ">
                                {fetchingDetailsId === ret.id ? (
                                  <div className="flex items-center gap-2 text-slate-400 text-xs py-4 font-bold">
                                    <Loader2
                                      className="animate-spin"
                                      size={16}
                                    />{" "}
                                    Fetching Details...
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-3 flex items-center gap-1.5">
                                      <Package size={12} /> Items logged in this
                                      draft
                                    </p>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="bg-slate-50 text-[9px] uppercase text-slate-500 font-black border-y border-slate-100">
                                          <th className="p-2 pl-3 text-left">
                                            Product
                                          </th>
                                          <th className="p-2 text-center">
                                            Qty Returned
                                          </th>
                                          <th className="p-2 text-right pr-3">
                                            Draft Refund Val
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-50">
                                        {(
                                          detailsCache[ret.id]?.items || []
                                        ).map((item, idx) => {
                                          console.log({item})
                                          return (
                                            <tr key={idx}>
                                              <td className="p-2 pl-3 font-bold text-slate-700">
                                                <div className="flex flex-col gap-1">
                                                  <span>{item.productName}</span>
                                                  <span className="text-[8px] text-gray-400">{item.brandName || ""}</span>
                                                </div>
                                              </td>
                                              <td className="p-2 text-center font-black text-slate-600">
                                                {item.quantityReturned}
                                              </td>
                                              <td className="p-2 text-right pr-3 font-black text-red-500">
                                                {formatCurrency(
                                                  item.refundAmount,
                                                )}
                                              </td>
                                            </tr>
                                          )})}
                                      </tbody>
                                    </table>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* ── FOR SALES ORDER TAB ── */}
            {activeTab === "ForSalesOrder" && (
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-widest">
                  <tr>
                    <th className="px-5 py-3.5 w-10"></th>
                    <th className="px-5 py-3.5">SGR-R #</th>
                    <th className="px-5 py-3.5">Source GR</th>
                    <th className="px-5 py-3.5">Vendor</th>
                    <th className="px-5 py-3.5 text-right">Cash Refund</th>
                    <th className="px-5 py-3.5">Return #</th>
                    <th className="px-5 py-3.5">Processed By</th>
                    <th className="px-5 py-3.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-14 text-slate-400 text-sm"
                      >
                        No processed returns yet.
                      </td>
                    </tr>
                  ) : (
                    paginated.map((ret) => (
                      <React.Fragment key={ret.id}>
                        <tr
                          onClick={() => handleExpandRow(ret)}
                          className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                        >
                          <td className="px-5 py-4 text-slate-400">
                            {expandedRow === ret.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-black text-slate-800 font-mono text-xs">
                              {ret.salesOrderReturnNumber}
                            </div>
                            <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded mt-1 inline-flex items-center gap-0.5 border border-emerald-200">
                              <CheckCircle size={8} /> Processed
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-indigo-700">
                                {ret.goodReceiptNumber}
                              </span>
                              <span className="text-[9px] font-black bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-0.5 uppercase">
                                <Lock size={8} /> Locked
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                              <User size={12} className="text-slate-400" />{" "}
                              {ret.vendorName}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right font-black text-red-600 text-base">
                            {formatCurrency(ret.totalRefundAmount)}
                          </td>
                          <td className="px-5 py-4">
                            <span className="font-mono text-xs bg-slate-100 text-slate-700 font-bold px-2 py-1 rounded border border-slate-200">
                              {ret.returnNumber || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="text-xs font-semibold text-slate-700">
                              {ret.createdByFullName || "—"}
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock size={9} /> {formatDate(ret.createdDate)}
                            </div>
                          </td>
                          <td
                            className="px-5 py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openDebitMemo(ret)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg text-[11px] font-bold uppercase hover:bg-orange-100 transition-all"
                              >
                                <FileText size={13} /> View Memo
                              </button>
                              <button
                                onClick={() => handleDeleteSOR(ret)}
                                disabled={pendingDeleteId === ret.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase border bg-red-50 text-red-600 border-red-200 hover:bg-red-600 hover:text-white transition-all"
                              >
                                <Trash2 size={13} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Details */}
                        {expandedRow === ret.id && (
                          <tr className="bg-slate-50/50">
                            <td colSpan={8} className="px-5 py-5 pl-16">
                              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm max-w-5xl">
                                {fetchingDetailsId === ret.id ? (
                                  <div className="flex items-center gap-2 text-slate-400 text-xs py-4 font-bold">
                                    <Loader2
                                      className="animate-spin"
                                      size={16}
                                    />{" "}
                                    Fetching Details...
                                  </div>
                                ) : (
                                  <>
                                    <h4 className="text-[11px] font-black uppercase text-slate-400 flex items-center gap-2 mb-4">
                                      <Package size={14} /> Items Breakdown —{" "}
                                      {ret.salesOrderReturnNumber}
                                    </h4>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs text-left">
                                        <thead className="bg-slate-100 text-[9px] uppercase text-slate-500 font-black">
                                          <tr>
                                            <th className="p-2.5 pl-4">
                                              Product
                                            </th>
                                            <th className="p-2.5 text-center">
                                              Qty Returned
                                            </th>
                                            <th className="p-2.5 text-center bg-red-50/50 text-red-500">
                                              Cash Return
                                            </th>
                                            <th className="p-2.5 text-center bg-blue-50/50 text-blue-500">
                                              Replace
                                            </th>
                                            <th className="p-2.5 text-right pr-4">
                                              Refund Value
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                          {(
                                            ret.items ||
                                            detailsCache[ret.id]?.items ||
                                            []
                                          ).map((item, idx) => (
                                            <tr
                                              key={idx}
                                              className="hover:bg-slate-50/50"
                                            >
                                              <td className="p-2.5 pl-4 font-bold text-slate-700">
                                                {item.productName}
                                              </td>
                                              <td className="p-2.5 text-center font-black text-slate-600">
                                                {item.quantityReturned}
                                              </td>
                                              <td className="p-2.5 text-center font-black text-red-600">
                                                {item.cashQty || 0}
                                              </td>
                                              <td className="p-2.5 text-center font-black text-blue-600">
                                                {item.replaceQty || 0}
                                              </td>
                                              <td className="p-2.5 text-right pr-4 font-black text-red-600">
                                                {formatCurrency(
                                                  item.refundAmount,
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    {ret.note && (
                                      <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2 text-xs">
                                        <span className="font-black uppercase text-slate-400">
                                          Note:
                                        </span>
                                        <span className="text-slate-600 italic">
                                          {ret.note}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        <SyncToAccountingModal 
          isOpen={showSyncToAccountingForm}
          onClose={closeSyncToAccountingForm}
          onConfirm={handleSyncToAccounting}
        />

        {!loading && (
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={currentList.length}
            currentPage={currentPage}
            paginate={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
